/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import type { TFunction } from 'i18next';

/**
 * The names of the errored fields, in the words Configurações > Imóveis uses
 * for them. A field outside that vocabulary has no name here.
 *
 * @param fields - The errored field keys, in form order.
 * @param t - A translator for the properties namespace.
 * @returns The names that exist.
 */
function fieldNames(fields: string[], t: TFunction): string[] {
  return fields
    .map((field) => t(`v2.imoveis.fields.${field}`, { ns: 'features/agency-settings', defaultValue: '' }))
    .filter(Boolean);
}

/**
 * What the drawer says when a save is blocked: the fields to fill, by name,
 * so the broker does not hunt for them; or, when none has a name (a value out
 * of range, say), to check the fields marked in red.
 *
 * @param fields - The errored field keys, in form order.
 * @param t - A translator for the properties namespace.
 * @param language - The language the list is joined in.
 * @returns The message.
 */
export function blockedSaveMessage(fields: string[], t: TFunction, language: string): string {
  const names = fieldNames(fields, t);
  if (names.length === 0) return t('form.drawer.fixFields');
  const list = new Intl.ListFormat(language, { type: 'conjunction' }).format(names);
  return t('form.drawer.missingFields', { fields: list });
}

/**
 * Scrolls the topmost errored field into view. Fields mark their wrapper with
 * `data-field`, since most of them are not plain inputs.
 *
 * @param fields - The errored field keys.
 */
export function revealFirstError(fields: string[]): void {
  const [target] = fields
    .map((field) => document.querySelector(`[data-field="${field}"]`))
    .filter((el): el is Element => el !== null)
    .sort((a, b) => a.getBoundingClientRect().top - b.getBoundingClientRect().top);
  target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
}
